import { graphql } from "gatsby";
import React, { useEffect } from "react";

import Primary from "~/components/layout/Primary";
import Seo from "~/components/Seo";
import DocumentTabTable from "~/components/DocumentTabTable.js";

import {
    ProductCategoryAlert,
    ProductCategoryBlockList,
    ProductCategoryBottomArea,
    ProductCategoryCarousel,
    ProductCategoryColumnCards,
    ProductCategoryCtaBar,
    ProductCategoryJumpLinks,
    ProductCategoryFeaturedResources,
    ProductCategoryKnowledgeCenter,
    ProductCategoryPageHeader,
    ProductCategoryTableSection,
} from "./components";

import { useFormContext } from "~/context/FormContext";

const ProductCategory = ({ data, location, pageContext }) => {
    const {
        parentCategory,
        wpProductCategory: { title },
    } = data;

    const { setHiddenFields } = useFormContext();
    const urlPermutations = pageContext.urlPermutations;

    useEffect(() => {
        setHiddenFields({
            subcategory: title,
            pageUrl: location.href,
            productUrl: location.href,
            pageTitle: title,
        });
    }, [setHiddenFields, location.href, parentCategory?.title, title]);

    return (
        <Primary isProductPage {...{ urlPermutations, pageCode: "en" }}>
            <ProductCategoryPageHeader
                data={data}
                urlPermutations={urlPermutations}
            />
            <ProductCategoryJumpLinks data={data} />
            <ProductCategoryFeaturedResources data={data} />
            <ProductCategoryColumnCards data={data} />
            <ProductCategoryAlert data={data} />
            <ProductCategoryTableSection data={data} />
            <ProductCategoryBlockList
                data={data}
                urlPermutations={urlPermutations}
            />
            <ProductCategoryCarousel data={data} />
            <DocumentTabTable data={data} />
            <ProductCategoryCtaBar data={data} />
            <ProductCategoryKnowledgeCenter data={data} />
            <ProductCategoryBottomArea data={data} />
        </Primary>
    );
};

export default ProductCategory;

export const Head = ({ data, location, pageContext }) => {
    const {
        wpProductCategory: { title, seo, language },
    } = data;

    const urlPermutations = pageContext.urlPermutations;

    return (
        <Seo
            {...{ seo, location, urlPermutations }}
            pageTitle={title}
            languageCode={language?.code}
        />
    );
};

export const query = graphql`
    query ProductCategoryQuery($slug: String!, $parentCategorySlug: String) {
        allWpTabLocation {
            nodes {
                slug
                name
                taxonomyDetails {
                    sortOrder
                }
            }
        }
        products: allWpProduct(
            filter: {
                globalAttributes: { typeId: { in: ["simple", "reference"] } }
                productCategories: {
                    nodes: { elemMatch: { slug: { eq: $slug } } }
                }
            }
        ) {
            nodes {
                ...ProductTableFragment
                ...ReferenceDesignProductFragment
            }
        }
        wpProductCategory(slug: { eq: $slug }) {
            id
            slug
            title: name
            description
            seo {
                ...SeoTaxonomyFragment
            }
            language {
                code
            }
            categoryData {
                tableFilters
                tableHeaders
                initialTableSortColumn
                initialTableSortDirection
                secondaryTableSortColumn
                secondaryTableSortDirection
                tertiaryTableSortColumn
                tertiaryTableSortDirection
                firstColumnStaticOnMobile
                bottomContentArea
                readMore
                pageHeaderSectionId
                noCategoryRedirectForGroupedProduct
                addPaginationToProductTable
                jumpLinks {
                    linkUrl
                    linkLabel
                    formId
                    fieldGroupName
                    action
                }
                jumpLinksNoMarginBottom
                featuredResources {
                    title
                    sectionId
                    includeImages
                    withBorder
                    resourceCards {
                        image {
                            altText
                            title
                            sourceUrl
                            localFile {
                                childImageSharp {
                                    gatsbyImageData(
                                        aspectRatio: 2.06
                                        layout: FULL_WIDTH
                                    )
                                    noAspectRatio: gatsbyImageData
                                }
                            }
                        }
                        title
                        resourceType
                        resources {
                            linkLabel
                            linkUrl
                            doc {
                                ...DocumentFragment
                            }
                            icon
                        }
                    }
                }
                productCarouselShowCaptions
                productCarouselImages {
                    altText
                    caption
                    id
                    link
                    mediaItemUrl
                    sourceUrl
                    title
                }
                columnCardsTitle
                columnCardsNoMarginBottom
                columnCardsPerRow
                columnCardsSectionId
                columnCards {
                    title
                    iconClass
                    content
                    fieldGroupName
                }
                landingPageLinks {
                    action
                    linkLabel
                    linkUrl
                    formId
                    isEmbed
                    video {
                        ...VideoFragment
                    }
                    videoUrl
                }
                displayDisclaimer
                disclaimer {
                    backgroundColor
                    copy
                    title
                    noMarginBottom
                    sectionId
                    iconName
                    closedByDefault
                }
                blocks {
                    ... on WpProductCategory_Categorydata_Blocks_EmbedBlock {
                        name: hidden
                        attributes {
                            caption
                            url
                            className
                        }
                        sectionId
                        sectionHeading
                    }
                    ... on WpProductCategory_Categorydata_Blocks_CustomHtmlBlock {
                        name: hidden
                        html: originalContent
                        sectionId
                        sectionHeading
                    }
                    ... on WpProductCategory_Categorydata_Blocks_VideoEmbedBlock {
                        name: hidden
                        videoEmbedBlock {
                            aspectRatio
                            caption
                            isEmbed
                            video {
                                ...VideoFragment
                            }
                            videoUrl
                            smallWidth
                            sectionId
                            sectionHeading
                        }
                    }
                    ... on WpProductCategory_Categorydata_Blocks_VideoPlaylistBlock {
                        name: hidden
                        videoPlaylistBlock {
                            sectionId
                            playlistBlock: playlist {
                                ... on WpVideoPlaylist {
                                    id
                                    title
                                    playlistPostType: playlist {
                                        hideTitle
                                        introCopy
                                        playlist {
                                            ... on WpDlmdownload {
                                                ...DocumentFragment
                                            }
                                        }
                                    }
                                }
                            }
                            styling {
                                borderTop
                                borderBottom
                                noMarginBottom
                            }
                        }
                    }
                    ... on WpProductCategory_Categorydata_Blocks_ContentBlock {
                        name: hidden
                        contentBlock {
                            content
                            sectionId
                            noMarginBottom
                            withBorder
                            backgroundColor
                            withPadding
                        }
                    }
                }
                productCarousel {
                    individualProducts {
                        product {
                            ... on WpProduct {
                                id
                                title
                                slug
                                productUrl
                                featuredImage {
                                    node {
                                        altText
                                        title
                                        sourceUrl
                                        localFile {
                                            childImageSharp {
                                                gatsbyImageData(
                                                    width: 215
                                                    height: 215
                                                    transformOptions: {
                                                        fit: CONTAIN
                                                    }
                                                    backgroundColor: "transparent"
                                                )
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                    links {
                        linkLabel
                        linkUrl
                    }
                    limit
                    subtitle
                    title
                    productCategory {
                        name
                        products {
                            nodes {
                                id
                                title
                                slug
                                productUrl
                                featuredImage {
                                    node {
                                        altText
                                        title
                                        sourceUrl
                                        localFile {
                                            childImageSharp {
                                                gatsbyImageData(
                                                    width: 215
                                                    height: 215
                                                    transformOptions: {
                                                        fit: CONTAIN
                                                    }
                                                    backgroundColor: "transparent"
                                                )
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
                knowledgeCenterArticles {
                    viewAllUrl
                    productLine {
                        slug
                        name
                        id
                    }
                    topic {
                        slug
                        name
                        id
                    }
                    type {
                        slug
                        name
                        id
                    }
                    individualArticles {
                        card: article {
                            ...ArticleFragment
                        }
                    }
                }
                knowledgeCenterSectionId
                productTableSectionId
                blocksSectionId
                productCarouselSectionId
                bottomContentSectionId
                categoryPageDocuments: categoryToDocument {
                    ... on WpDlmdownload {
                        ...DocumentFragment
                    }
                }
                categoryPageArticles: categoryToArticles {
                    ... on WpArticle {
                        ...ArticleFragment
                    }
                }
                tabSectionTitle
                tabSectionId
                tabs {
                    content: tabContent
                    title: tabTitle
                }
                blockDiagram {
                    tabTitle
                    image {
                        altText
                        title
                        sourceUrl
                    }
                    openByDefault
                    hotspots {
                        linkLabel
                        linkUrl
                        title
                        copy
                        hexOverride
                        image {
                            altText
                            title
                            sourceUrl
                        }
                        location {
                            left
                            top
                        }
                    }
                }
            }
            referenceDesignsCategory {
                showByApplicationTableView
                toggleLabel
                applicationTabLabel
                productTabLabel
                referenceDesignsTable {
                    columnsAttribute
                    tableKeyAttribute
                    referenceDesignTableFilters
                    referenceDesignApplications
                    refDesignProductOverrides: referenceDesignProducts {
                        ... on WpProduct {
                            ...ProductTableFragment
                            ...ReferenceDesignProductFragment
                        }
                    }
                }
            }
        }
        parentCategory: wpProductCategory(slug: { eq: $parentCategorySlug }) {
            id
            slug
            title: name
            categoryData {
                tableFilters
                tableHeaders
                knowledgeCenterArticles {
                    viewAllUrl
                    productLine {
                        slug
                        name
                        id
                    }
                    topic {
                        slug
                        name
                        id
                    }
                    type {
                        slug
                        name
                        id
                    }
                    individualArticles {
                        card: article {
                            ...ArticleFragment
                        }
                    }
                }
            }
        }
    }
`;
